import React, { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import { Tooltip, useMediaQuery } from '@chakra-ui/react'
import { clsx } from 'clsx'

import type { ApplyV2SettingsSectionName, NavItemsState } from '@core/app/types/applyV2Type'
import ApplyNavBtn from '@core/components/ApplyDialogV2/ApplyNavBtn'
import ApplySkeleton from '@core/components/ApplyDialogV2/ApplySkeleton'
import ApplyV2Content from '@core/components/ApplyDialogV2/ApplyV2Content'
import ApplyV2Tooltip from '@core/components/ApplyDialogV2/ApplyV2Tooltip'
import CloseApplyModal from '@core/components/ApplyDialogV2/CloseApplyModal'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import './ApplyDialogV2.scss'

const baseClass = 'ApplyDialogV2'

interface ApplyDialogV2Props {
    className?: string
    children?: ReactNode
}

function ApplyDialogV2({ className = '', children }: ApplyDialogV2Props) {
    const {
        onOpenApply,
        isExpanded,
        isOpen,
        onCloseApply,
        onResizeApply,
        settingsData,
        settingsRefetch,
        settingsError,
        storageData,
        isSettingsFetching,
        isPostFetching,
        postData,
        onSelectStep,
        navItems,
        isFinalStep,
        existingSteps,
        isReviewFinalState,
        postInfoBackgroundColor,
    } = useApplyV2Context()
    const nameRef = useRef<HTMLSpanElement>(null)
    const navRef = useRef<HTMLDivElement>(null)
    const dialogRef = useRef<HTMLDivElement>(null)
    const [isEllipsed, setIsEllipsed] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isCursorInside, setIsCursorInside] = useState(false)
    const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 })
    const [isDesktop] = useMediaQuery('(min-width: 800px)')
    const [isMobile] = useMediaQuery('(max-width: 1229px)')

    const sliderRef = useRef<HTMLDivElement>(null)
    // const [activeIndex, setActiveIndex] = useState(0)
    const [activeSlide, setActiveSlide] = useState<ApplyV2SettingsSectionName | null>(null)
    const { getTitle } = useApplyV2NavTranslations()

    const scrollToSlide = (slide: ApplyV2SettingsSectionName | null) => {
        if (!sliderRef.current) return
        const slideWidth = sliderRef.current.clientWidth
        const index = navItems.findIndex((item) => item.name === slide)
        sliderRef.current.scrollTo({
            left: index * slideWidth,
            // @ts-ignore
            behavior: 'instant',
        })
        setActiveSlide(slide)
    }

    const getStepTitle = useMemo(() => {
        return (
            navItems.find((item) => item.name === storageData?.currentStep)?.title ??
            [...navItems].sort((a, b) => a.order - b.order)[0]?.title
        )
    }, [navItems, storageData?.currentStep])

    // useEffect(() => {
    //     const navElement = navRef.current
    //     if (navElement) {
    //         const hasHorizontalScroll = navElement.scrollWidth > navElement.clientWidth
    //         console.log('Client Width:', navElement.clientWidth)
    //         console.log('Scroll Width:', navElement.scrollWidth)
    //         console.log('Has horizontal scroll:', hasHorizontalScroll)
    //         console.log('Scrollable width:', navElement.scrollWidth - navElement.clientWidth)
    //     }
    // }, [storageData?.title, navItems])

    // useEffect(() => {
    //     if (isOpen && isExpanded) {
    //         scrollToSlide(storageData?.currentStep ?? navItems[0].name)
    //     }
    // }, [storageData, navItems, isOpen, isExpanded])

    useEffect(() => {
        if (!isOpen || !isExpanded || !sliderRef.current || !navItems[0]) return

        const observer = new ResizeObserver(() => {
            scrollToSlide(storageData?.currentStep ?? navItems[0]?.name)
        })

        observer.observe(sliderRef.current)

        return () => {
            observer.disconnect()
        }
    }, [storageData, navItems, isOpen, isExpanded])

    useEffect(() => {
        const handleResize = () => {
            scrollToSlide(activeSlide)
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [activeSlide])

    const isLoading = isSettingsFetching || isPostFetching

    useEffect(() => {
        const el = nameRef.current
        if (el) {
            setIsEllipsed(el.scrollWidth > el.clientWidth)
        }
    }, [isOpen, isExpanded, postData])

    // useEffect(() => {
    //     const handleResize = () => {
    //         const el = nameRef.current
    //         if (el) {
    //             setIsEllipsed(el.scrollWidth > el.clientWidth)
    //         }
    //     }
    //
    //     window.addEventListener('resize', handleResize)
    //     handleResize()
    //
    //     return () => window.removeEventListener('resize', handleResize)
    // }, [])

    useEffect(() => {
        if (isOpen && isExpanded) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = ''
        }

        return () => {
            document.body.style.overflow = ''
        }
    }, [isOpen, isExpanded])

    useEffect(() => {
        if (!isOpen || !dialogRef.current) return

        const handlePointerMove = (event: PointerEvent) => {
            const rect = dialogRef.current?.getBoundingClientRect()
            if (!rect) return

            setCursorPosition({
                x: event.clientX - rect.left,
                y: event.clientY - rect.top,
            })
        }

        const handlePointerLeave = () => {
            setIsCursorInside(false)
            setCursorPosition({ x: -1000, y: -1000 })
        }

        const handlePointerEnter = () => {
            setIsCursorInside(true)
        }

        const element = dialogRef.current
        element.addEventListener('pointermove', handlePointerMove)
        element.addEventListener('pointerenter', handlePointerEnter)
        element.addEventListener('pointerleave', handlePointerLeave)

        return () => {
            element.removeEventListener('pointermove', handlePointerMove)
            element.removeEventListener('pointerenter', handlePointerEnter)
            element.removeEventListener('pointerleave', handlePointerLeave)
        }
    }, [isOpen])

    useEffect(() => {
        if (isOpen && postData) {
            window.dispatchEvent(
                new CustomEvent('ra-button-job-post-action-apply-now', {
                    detail: {
                        job_post_id: postData?.ad?.AdID,
                    },
                })
            )
        }
    }, [isOpen, postData])

    const currentItemName = storageData?.currentStep
    const currentItemIdx = navItems.findIndex((item) => item.name === currentItemName)

    const existingStepIndices = existingSteps
        .map((step) => navItems.findIndex((item) => item.name === step))
        .filter((index) => index !== -1)

    let maxExistingStepIndex = existingStepIndices.length > 0 ? Math.max(...existingStepIndices) : -1

    if (currentItemIdx > maxExistingStepIndex) {
        maxExistingStepIndex = currentItemIdx
    }

    const navItemsWithDisabled = navItems.map((item, index) => {
        if (index <= maxExistingStepIndex) {
            return item
        }

        return {
            ...item,
            //todo add enabled - for dev
            state: 'disabled' as NavItemsState,
        }
    })

    // console.log(process.env.NODE_ENV)

    const stepValues = useMemo(() => {
        const activeIndex = navItems.findIndex((item) => item.name === currentItemName)
        // return `${activeIndex + 1}/${navItems.length}`
        return `${getTitle('11890', 'Step {{current}} of {{total}}', {
            current: activeIndex + 1 > 0 ? activeIndex + 1 : 1,
            total: navItems.length,
        })}:`
    }, [navItems, getTitle, currentItemName])

    const borderColor = !isExpanded
        ? postInfoBackgroundColor
            ? `3px solid ${postInfoBackgroundColor}`
            : '3px solid rgba(47, 128, 237, 0.30)'
        : 'none'

    if (!isOpen) return null

    return (
        <div
            ref={dialogRef}
            onClick={(e) => {
                if (!isExpanded) {
                    onResizeApply()
                }
            }}
            className={clsx(baseClass, {
                [`${baseClass}__expanded`]: isExpanded,
                [`${baseClass}__opened`]: isOpen,
                [className]: !!className,
            })}
            style={{
                borderTop: borderColor,
                borderLeft: borderColor,
                borderRight: borderColor,
                ['--cursor-x' as string]: `${cursorPosition.x}px`,
                ['--cursor-y' as string]: `${cursorPosition.y}px`,
            }}
            data-postInfoBackgroundColor={postInfoBackgroundColor}
        >
            <div
                className={clsx(`${baseClass}__cursor-glow`, {
                    [`${baseClass}__cursor-glow--visible`]: isOpen && !isExpanded && isCursorInside,
                })}
            />
            <CloseApplyModal isModalOpen={isModalOpen} onCloseApply={onCloseApply} setIsModalOpen={setIsModalOpen} />
            <div
                className={clsx(`${baseClass}__header`, {
                    [`${baseClass}__header--opened`]: isOpen,
                    [`${baseClass}__header--expanded`]: isExpanded,
                })}
            >
                <ApplySkeleton isLoading={false} style={{ width: '44px', height: '44px' }}>
                    {/*<Tooltip*/}
                    {/*    hasArrow*/}
                    {/*    isDisabled={isLoading || isMobile}*/}
                    {/*    openDelay={500}*/}
                    {/*    label={isExpanded ? 'Collapse' : 'Expand'}*/}
                    {/*    // placement={isExpanded ? 'top-start' : 'right-start'}*/}
                    {/*    placement={'top'}*/}
                    {/*    padding={'8px 16px'}*/}
                    {/*    borderRadius={'4px'}*/}
                    {/*    backgroundColor={'#19191A'}*/}
                    {/*    fontWeight={600}*/}
                    {/*    fontSize={'12px'}*/}
                    {/*    zIndex={9999}*/}
                    {/*>*/}
                    <ApplyV2Tooltip
                        label={isExpanded ? getTitle('7837', 'Collapse') : getTitle('6939', 'Expand')}
                        placement={isExpanded ? 'bottom-start' : 'top-start'}
                        isDisabled={isLoading || isMobile}
                        display={'inline-block'}
                    >
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation()
                                onResizeApply()
                            }}
                            className={clsx(
                                `${baseClass}__header__expand-btn`,
                                `${baseClass}__header__expand-btn-expand`,
                                `${baseClass}__header__expand-btn-expand--isMobile`,
                                {
                                    [`${baseClass}__header__expand-btn-expand--isOpened`]: isOpen,
                                    [`${baseClass}__header__expand-btn-expand--isExpanded`]: isExpanded,
                                    [`${baseClass}__header__expand-btn-expand--isMobile`]: isMobile,
                                }
                            )}
                        >
                            {/*{isExpanded ? (*/}
                            {/*    <i className="fa-regular fa-chevron-down"></i>*/}
                            {/*) : (*/}
                            {/*    <span className={`${baseClass}__header__expand-btn-expand--mobile`}>*/}
                            {/*        <i className="fa-regular fa-chevron-up"></i>*/}
                            {/*        <span className={`${baseClass}__header__expand-btn-expand--mobile-text`}>*/}
                            {/*            {getTitle('10455', 'Tap to continue your application')}*/}
                            {/*        </span>*/}
                            {/*    </span>*/}
                            {/*)}*/}
                            {isExpanded ? (
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M1.33398 7.99992C1.33398 7.63173 1.63246 7.33325 2.00065 7.33325H14.0007C14.3688 7.33325 14.6673 7.63173 14.6673 7.99992C14.6673 8.36811 14.3688 8.66659 14.0007 8.66659H2.00065C1.63246 8.66659 1.33398 8.36811 1.33398 7.99992Z"
                                        fill="#77777C"
                                    />
                                </svg>
                            ) : (
                                <span className={`${baseClass}__header__expand-btn-expand--mobile`}>
                                    <svg
                                        width="28"
                                        height="28"
                                        viewBox="0 0 28 28"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            className="expand-icon__bg"
                                            d="M0 14C0 6.26801 6.26801 0 14 0C21.732 0 28 6.26801 28 14C28 21.732 21.732 28 14 28C6.26801 28 0 21.732 0 14Z"
                                            fill="#2F80ED"
                                        />
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M14.0007 7.3335C14.3688 7.3335 14.6673 7.63197 14.6673 8.00016V13.1266H20.0007C20.3688 13.1266 20.6673 13.4251 20.6673 13.7933C20.6673 14.1615 20.3688 14.4599 20.0007 14.4599H14.6673V20.0002C14.6673 20.3684 14.3688 20.6668 14.0007 20.6668C13.6325 20.6668 13.334 20.3684 13.334 20.0002V14.4599H8.00065C7.63246 14.4599 7.33398 14.1615 7.33398 13.7933C7.33398 13.4251 7.63246 13.1266 8.00065 13.1266H13.334V8.00016C13.334 7.63197 13.6325 7.3335 14.0007 7.3335Z"
                                            fill="white"
                                        />
                                    </svg>
                                    <span className={`${baseClass}__header__expand-btn-expand--mobile-text`}>
                                        {getTitle('10455', 'Tap to continue your application')}
                                    </span>
                                </span>
                            )}
                        </button>
                    </ApplyV2Tooltip>
                    {/*</Tooltip>*/}
                </ApplySkeleton>
                <div
                    className={clsx(`${baseClass}__header__left`, {
                        [`${baseClass}__header__left--opened`]: isOpen,
                        [`${baseClass}__header__left--expanded`]: isExpanded,
                    })}
                >
                    <div className={`${baseClass}__header__title`}>
                        {/*{!isEllipsed && (*/}
                        {/*    <ApplySkeleton isLoading={isPostFetching}>*/}
                        {/*        <span className={`${baseClass}__header__title--application`}>*/}
                        {/*            {t('1589', 'Application')}*/}
                        {/*        </span>*/}
                        {/*    </ApplySkeleton>*/}
                        {/*)}*/}
                        {isExpanded ? (
                            <ApplySkeleton isLoading={isPostFetching} style={{ maxWidth: '240px' }}>
                                <span className={`${baseClass}__header__title--name`}>
                                    <Tooltip
                                        hasArrow
                                        isDisabled={isLoading}
                                        openDelay={500}
                                        label={''}
                                        // label={'We are searching for an exceptionally skilled Marketing Coordinator'}
                                        // placement={'right'}
                                        padding={'8px 16px'}
                                        borderRadius={'4px'}
                                        backgroundColor={'#19191A'}
                                        fontWeight={600}
                                        fontSize={'12px'}
                                    >
                                        <span className={`${baseClass}__header__title--name-value`} ref={nameRef}>
                                            {storageData?.title}
                                        </span>
                                    </Tooltip>
                                </span>
                            </ApplySkeleton>
                        ) : (
                            <ApplySkeleton
                                isLoading={isPostFetching || isSettingsFetching}
                                style={{ maxWidth: '240px' }}
                            >
                                <span className={`${baseClass}__header__title--name`}>
                                    {getTitle('11868', 'Continue application')}
                                </span>
                                <span className={`${baseClass}__header__title--dot`}></span>
                                <span className={`${baseClass}__header__title--steps`}>{stepValues}</span>
                                <span className={`${baseClass}__header__title--current`}>{getStepTitle}</span>
                            </ApplySkeleton>
                        )}
                    </div>
                </div>

                {!isExpanded ? (
                    <ApplySkeleton isLoading={isLoading} style={{ width: '44px', height: '44px' }}>
                        {/*<Tooltip*/}
                        {/*    hasArrow*/}
                        {/*    isDisabled={isLoading}*/}
                        {/*    openDelay={500}*/}
                        {/*    label={getTitle('10245', 'Close application')}*/}
                        {/*    placement={'left-start'}*/}
                        {/*    padding={'8px 16px'}*/}
                        {/*    borderRadius={'4px'}*/}
                        {/*    backgroundColor={'#19191A'}*/}
                        {/*    fontWeight={600}*/}
                        {/*    fontSize={'12px'}*/}
                        {/*>*/}
                        <ApplyV2Tooltip
                            label={getTitle('11957', 'Exit application')}
                            placement={'top-end'}
                            isDisabled={isLoading || isMobile}
                        >
                            <button
                                className={`${baseClass}__header__expand-btn ${baseClass}__header__expand-btn-close`}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setIsModalOpen(true)
                                }}
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        className="trash-icon__bg"
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M6.49422 1.54874C6.47311 1.54874 6.44676 1.55724 6.42273 1.58238C6.39776 1.60848 6.379 1.64974 6.379 1.69842V3.42376H9.62287V1.69842C9.62287 1.64974 9.60411 1.60848 9.57915 1.58238C9.55511 1.55724 9.52876 1.54874 9.50765 1.54874H6.49422ZM10.8991 3.42376V1.69842C10.8991 1.32922 10.7592 0.969704 10.5016 0.700318C10.243 0.429961 9.88615 0.272461 9.50765 0.272461H6.49422C6.11573 0.272461 5.75884 0.429961 5.50031 0.700318C5.24271 0.969704 5.10273 1.32922 5.10273 1.69842V3.42376H2.73463C2.7303 3.42372 2.72597 3.42372 2.72163 3.42376H1.97408C1.62164 3.42376 1.33594 3.70947 1.33594 4.0619C1.33594 4.41433 1.62164 4.70004 1.97408 4.70004H2.13812L2.84282 13.5433C2.84965 14.1117 3.06832 14.6605 3.46089 15.071C3.8607 15.4891 4.40921 15.7296 4.98751 15.7296H11.0144C11.5927 15.7296 12.1412 15.4891 12.541 15.071C12.9335 14.6605 13.1522 14.1117 13.1591 13.5433L13.8638 4.70004H14.0278C14.3802 4.70004 14.6659 4.41433 14.6659 4.0619C14.6659 3.70947 14.3802 3.42376 14.0278 3.42376H13.2802C13.2759 3.42372 13.2716 3.42372 13.2672 3.42376H10.8991ZM3.41845 4.70004L4.11691 13.4651C4.11826 13.482 4.11893 13.4989 4.11893 13.5158C4.11893 13.7734 4.21706 14.0151 4.38331 14.1889C4.54862 14.3618 4.76659 14.4533 4.98751 14.4533H11.0144C11.2353 14.4533 11.4533 14.3618 11.6186 14.1889C11.7848 14.0151 11.8829 13.7734 11.8829 13.5158C11.8829 13.4989 11.8836 13.482 11.885 13.4651L12.5834 4.70004H3.41845ZM6.49422 6.57506C6.84666 6.57506 7.13236 6.86077 7.13236 7.2132V11.9402C7.13236 12.2926 6.84666 12.5783 6.49422 12.5783C6.14179 12.5783 5.85608 12.2926 5.85608 11.9402V7.2132C5.85608 6.86077 6.14179 6.57506 6.49422 6.57506ZM9.50765 6.57506C9.86009 6.57506 10.1458 6.86077 10.1458 7.2132V11.9402C10.1458 12.2926 9.86009 12.5783 9.50765 12.5783C9.15522 12.5783 8.86951 12.2926 8.86951 11.9402V7.2132C8.86951 6.86077 9.15522 6.57506 9.50765 6.57506Z"
                                        fill="#77777C"
                                    />
                                </svg>
                            </button>
                        </ApplyV2Tooltip>
                        {/*</Tooltip>*/}
                    </ApplySkeleton>
                ) : (
                    <div
                        ref={navRef}
                        className={`${baseClass}__header__nav`}
                        style={{ display: isReviewFinalState ? 'none' : 'flex' }}
                    >
                        {/*<ApplyNavBtn>Default</ApplyNavBtn>*/}
                        {/*<ApplyNavBtn>Text</ApplyNavBtn>*/}
                        {/*<ApplyNavBtn state={'optional'}>Optional</ApplyNavBtn>*/}
                        {/*<ApplyNavBtn state={'active'}>Active</ApplyNavBtn>*/}
                        {/*<ApplyNavBtn state={'success'}>Success</ApplyNavBtn>*/}
                        {/*<ApplyNavBtn state={'error'}>Error</ApplyNavBtn>*/}
                        {/*<ApplyNavBtn state={'error'} isLoading={true}>*/}
                        {/*    Error*/}
                        {/*</ApplyNavBtn>*/}
                        {/*<div className={`${baseClass}__header__nav-items`}>*/}
                        {isSettingsFetching
                            ? Array.from({ length: 5 }).map((_, i) => (
                                  <ApplyNavBtn key={i} isLoading={isSettingsFetching} />
                              ))
                            : navItemsWithDisabled.map((item) => (
                                  <ApplyNavBtn
                                      key={item.name}
                                      name={item.name}
                                      tooltipLabel={'Go to step'}
                                      isLoading={isSettingsFetching}
                                      onClick={() => onSelectStep(item.name)}
                                      state={item.name === activeSlide ? 'active' : item.state}
                                      data-nav-item={item.name}
                                  >
                                      {item.title}
                                  </ApplyNavBtn>
                              ))}
                        {/*</div>*/}
                    </div>
                )}
            </div>
            {/*<div className={clsx(`${baseClass}__content`, { [`${baseClass}__content--expanded`]: isExpanded })}>*/}
            {/*    <div className={`${baseClass}__slider-wrapper`} ref={sliderRef}>*/}
            {/*        {navItems.map((item, index) => (*/}
            {/*            <div key={item.name} className={`${baseClass}__slide`}>*/}
            {/*                <div> Слайд: {item.name}</div>*/}
            {/*            </div>*/}
            {/*        ))}*/}
            {/*    </div>*/}
            {/*</div>*/}

            <ApplyV2Content
                navItems={navItems}
                // activeIndex={activeIndex}
                currentStep={activeSlide}
                sliderRef={sliderRef}
                isExpanded={isExpanded}
                isLoading={isLoading}
                settingsData={settingsData}
                isFinalStep={isFinalStep}
            />
        </div>
    )
}

export default ApplyDialogV2
